"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { Search, Filter, X, ZoomIn } from "lucide-react";
import { SUBSPECIALTIES } from "@/lib/constants";

interface ImageItem {
  src: string;
  alt: string;
  caption: string;
  type: string;
  diseaseSlug: string;
  diseaseTitle: string;
  subspecialty: string;
  category: string;
}

const IMAGE_TYPES = [
  { id: "all", label: "전체" },
  { id: "fundus", label: "Fundus" },
  { id: "slit-lamp", label: "Slit Lamp" },
  { id: "oct", label: "OCT" },
  { id: "clinical-photo", label: "Clinical Photo" },
  { id: "external", label: "External" },
  { id: "surgical", label: "Surgical" },
  { id: "anterior-segment", label: "Anterior Seg" },
  { id: "ubm", label: "UBM" },
  { id: "histology", label: "Histology" },
  { id: "radiology", label: "Radiology" },
  { id: "visual-field", label: "Visual Field" },
  { id: "imaging", label: "Imaging" },
  { id: "gonioscopy", label: "Gonioscopy" },
  { id: "diagram", label: "Diagram" },
];

// Cache buster for images - changes on each page load
const IMG_CACHE_BUSTER = `v=${Date.now()}`;

export function ImageExplorer() {
  const [images, setImages] = useState<ImageItem[]>([]);
  const [search, setSearch] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedSub, setSelectedSub] = useState("all");
  const [lightbox, setLightbox] = useState<ImageItem | null>(null);
  const closeLightbox = useCallback(() => setLightbox(null), []);

  // Escape key to close lightbox
  useEffect(() => {
    if (!lightbox) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, closeLightbox]);

  useEffect(() => {
    fetch(`/image-index.json?v=${Date.now()}`, { cache: "no-store" })
      .then((r) => r.json())
      .then(setImages)
      .catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return images.filter((img) => {
      if (selectedType !== "all" && img.type !== selectedType) return false;
      if (selectedSub !== "all" && img.subspecialty !== selectedSub) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          img.caption.toLowerCase().includes(q) ||
          img.alt.toLowerCase().includes(q) ||
          img.diseaseTitle.toLowerCase().includes(q) ||
          img.category.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [images, search, selectedType, selectedSub]);

  const typeLabels: Record<string, string> = {
    fundus: "Fundus",
    "slit-lamp": "Slit Lamp",
    oct: "OCT",
    fag: "FAG",
    "clinical-photo": "Clinical",
    surgical: "Surgical",
    external: "External",
    "anterior-segment": "Anterior Seg",
    ubm: "UBM",
    histology: "Histology",
    radiology: "Radiology",
    "visual-field": "Visual Field",
    imaging: "Imaging",
    gonioscopy: "Gonioscopy",
    diagram: "Diagram",
  };

  return (
    <>
      <div className="space-y-4">
        {/* Filters */}
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="질환명, 이미지 설명 검색..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border bg-background pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <div className="flex flex-wrap gap-1">
                {IMAGE_TYPES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setSelectedType(t.id)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      selectedType === t.id
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <select
              value={selectedSub}
              onChange={(e) => setSelectedSub(e.target.value)}
              className="rounded-lg border bg-background px-3 py-1.5 text-xs outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="all">전체 분과</option>
              {SUBSPECIALTIES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="text-sm text-muted-foreground">
          {filtered.length}개 이미지
        </div>

        {/* Image Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            조건에 맞는 이미지가 없습니다
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((img, idx) => (
              <div
                key={`${img.src}-${idx}`}
                className="group cursor-pointer overflow-hidden rounded-xl border border-border bg-card hover:border-primary/30 transition-colors"
                onClick={() => setLightbox(img)}
              >
                <div className="relative aspect-[4/3] bg-black">
                  <img
                    src={`${img.src}?${IMG_CACHE_BUSTER}`}
                    alt={img.alt}
                    className="h-full w-full object-contain"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
                    <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-80 transition-opacity" />
                  </div>
                </div>
                <div className="p-2.5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary uppercase">
                      {typeLabels[img.type] || img.type}
                    </span>
                    <span className="text-[10px] text-muted-foreground truncate">
                      {SUBSPECIALTIES.find((s) => s.id === img.subspecialty)?.name || img.subspecialty}
                    </span>
                  </div>
                  <p className="text-xs font-medium text-foreground line-clamp-2 leading-snug">
                    {img.diseaseTitle.split(" (")[0]}
                  </p>
                  <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">
                    {img.caption}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.alt}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            aria-label="닫기"
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 transition-colors z-10"
          >
            <X className="h-6 w-6" />
          </button>
          <div
            className="max-h-[90vh] max-w-[90vw] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`${lightbox.src}?${IMG_CACHE_BUSTER}`}
              alt={lightbox.alt}
              className="max-h-[75vh] max-w-[90vw] object-contain rounded-lg"
            />
            <div className="mt-3 text-center max-w-lg">
              <p className="text-sm text-white font-medium">
                {lightbox.diseaseTitle}
              </p>
              <p className="text-sm text-white/70 mt-1">
                {lightbox.caption}
              </p>
              <a
                href={`/${lightbox.subspecialty}/${lightbox.diseaseSlug}`}
                className="inline-block mt-2 text-xs text-primary hover:underline"
              >
                질환 페이지로 이동 →
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
